import React, { useEffect, useRef, useState } from 'react';
import type { Stripe, StripeCardElement, StripeAddressElement } from '@stripe/stripe-js';
import { Modal } from '../../components/Modal';
import { ModalFooter } from '../../components/ModalFooter';
import { MsComboBox } from '../../components/MsComboBox';
import { useCreateProject } from '../../hooks/useProjects';
import { getConfigService } from '../../services/angular-bridge';
import type { Organization } from '../../hooks/useOrganization';
import { useIsInternalUser } from '../../hooks/useIsInternalUser';
import { AlertBlock } from '../../components/AlertBlock';

const USAGE_TYPES = [
  { id: 'processing', name: 'Processing' },
  { id: 'eca', name: 'ECA' },
  { id: 'review', name: 'Review' },
];

interface Props {
  organization?: Organization | null;
  onClose: () => void;
  onCreated?: () => void;
}

export function NewProjectModal({ organization, onClose, onCreated }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const cardElRef = useRef<StripeCardElement | null>(null);
  const addressElRef = useRef<StripeAddressElement | null>(null);

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [companyFirmName, setCompanyFirmName] = useState(organization?.name ?? '');
  const [promoCode, setPromoCode] = useState('');
  const [usageType, setUsageType] = useState<typeof USAGE_TYPES>([]);
  const [usageTypeTouched, setUsageTypeTouched] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [addressReady, setAddressReady] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPricing, setAcceptPricing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createProject = useCreateProject();
  const isInternalUser = useIsInternalUser();

  const needsStripe = !organization;

  useEffect(() => {
    if (!needsStripe) return;
    let cardEl: StripeCardElement | null = null;
    (async () => {
      const { loadStripe } = await import('@stripe/stripe-js');
      const pubKey = getConfigService().getStripePubKey();
      const stripe = await loadStripe(pubKey);
      if (!stripe || !cardRef.current || !addressRef.current) return;
      stripeRef.current = stripe;

      const appearance: any = {
        labels: 'floating',
        variables: {
          colorPrimary: '#0570de',
          colorBackground: '#ffffff',
          colorText: '#555555',
          colorTextPlaceholder: '#555555',
          gridRowSpacing: '4px',
          borderRadius: '4px',
          fontSizeBase: '14px',
        },
      };
      const elements = stripe.elements({ appearance });
      const addressEl = elements.create('address', { mode: 'billing' });
      cardEl = elements.create('card', {
        disableLink: true,
        hidePostalCode: true,
        classes: { base: 'stripe-card', focus: 'stripe-card--focused' },
        style: { base: { color: '#555555', fontSize: '14px', '::placeholder': { color: '#555555' } } },
      });
      cardElRef.current = cardEl;

      cardEl.mount(cardRef.current);
      addressEl.mount(addressRef.current);

      cardEl.on('change', (e: any) => setCardReady(e.complete));
      addressEl.on('change', (e: any) => setAddressReady(e.complete));

      addressElRef.current = addressEl;
    })();

    return () => { cardEl?.unmount(); addressElRef.current?.unmount(); };
  }, [needsStripe]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    const base = {
      name, description, companyFirmName: organization?.name ?? companyFirmName, promoCode,
      usageType: usageType[0]?.id ?? '',
      organizationId: organization?.id ?? null,
    };

    if (needsStripe) {
      const stripe = stripeRef.current;
      const cardEl = cardElRef.current;
      const addressEl = addressElRef.current;
      if (!stripe || !cardEl || !addressEl) { setSaving(false); return; }

      const address = await addressEl.getValue();
      if (!address.complete) { setSaving(false); return; }

      const result = await stripe.createToken(cardEl, {
        name: address.value.name,
        address_line1: address.value.address.line1,
        address_line2: address.value.address.line2 ?? '',
        address_city: address.value.address.city,
        address_state: address.value.address.state,
        address_zip: address.value.address.postal_code,
        address_country: address.value.address.country,
      });

      if (result.error) {
        setError(result.error.message ?? 'Card error');
        setSaving(false);
        return;
      }

      createProject.mutate({ ...base, token: result.token?.id ?? '' }, {
        onSuccess: () => { onCreated?.(); onClose(); },
        onError: () => { setError('Failed to create project.'); setSaving(false); },
      });
    } else {
      createProject.mutate({ ...base, token: '' }, {
        onSuccess: () => { onCreated?.(); onClose(); },
        onError: () => { setError('Failed to create project.'); setSaving(false); },
      });
    }
  };

  const nameInvalid = name.trim().length === 0 || name.length > 255;
  const canSubmit =
    !nameInvalid &&
    usageType.length > 0 &&
    acceptTerms &&
    acceptPricing &&
    (!needsStripe || (cardReady && addressReady));

  return (
    <Modal
      title="Create a new Project"
      testId="project-list-new-project-modal"
      icon="fas fa-gavel"
      onClose={onClose}
      footer={
        <ModalFooter
          onClose={onClose}
          onNext={handleSubmit}
          nextLabel="Save"
          nextDisabled={!canSubmit}
          saving={saving}
          savingLabel="Submitting..."
        />
      }
    >
      {isInternalUser && !organization && (
        <AlertBlock type="warning" icon="fas fa-exclamation-triangle">
          If this project is for an AccessID client, create it in AccessID instead — projects created here cannot
          be connected to an AccessID client.
        </AlertBlock>
      )}

      {organization && (
        <div className="form-group">
          <h4>Organization: <small><i className="fas fa-building" /> {organization.name}</small></h4>
        </div>
      )}

      <div className={`form-group${nameTouched && nameInvalid ? ' has-error' : ''}`}>
        {nameTouched && name.trim().length === 0 && (
          <span className="help-block">Name is required</span>
        )}
        {nameTouched && name.length > 255 && (
          <span className="help-block">Name must be less than 255 characters</span>
        )}
        <input
          type="text"
          placeholder="Project Name"
          data-testid="project-list-new-project-name-input"
          className="form-control"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
          disabled={saving}
          required
          maxLength={255}
        />
      </div>

      <div className="form-group">
        <input
          type="text"
          placeholder="Project Description"
          data-testid="project-list-new-project-description-input"
          className="form-control"
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={saving}
          maxLength={255}
        />
      </div>

      <div className="form-group">
        <input
          type="text"
          placeholder="Project Company or Firm"
          data-testid="project-list-new-project-company-input"
          className="form-control"
          value={companyFirmName}
          onChange={e => setCompanyFirmName(e.target.value)}
          disabled={saving}
          readOnly={!!organization}
          maxLength={255}
        />
      </div>

      <div className={`form-group${usageTypeTouched && usageType.length === 0 ? ' has-error' : ''}`}>
        {usageTypeTouched && usageType.length === 0 && <span className="help-block">Usage Type is required</span>}
        <MsComboBox
          id="new-project-usage-type"
          selected={usageType}
          onChange={items => { setUsageTypeTouched(true); setUsageType(items.slice(-1)); }}
          items={USAGE_TYPES}
          getKey={u => u.id}
          getLabel={u => u.name}
          placeholder="Project Usage Type"
          testId="project-list-new-project-usage-type-select"
        />
      </div>

      <div className="row">
        <div className="col-md-5">
          <div className="form-group">
            <input
              type="text"
              placeholder="Promo Code"
              data-testid="project-list-new-project-promo-code-input"
              className="form-control"
              value={promoCode}
              onChange={e => setPromoCode(e.target.value)}
              disabled={saving}
              maxLength={255}
            />
          </div>
        </div>
      </div>

      <br />

      {needsStripe && (
        <div className="form-group">
          <i className="fab fa-cc-visa fa-2x" style={{ color: '#cccccc' }} />
          {' '}
          <i className="fab fa-cc-mastercard fa-2x" style={{ color: '#cccccc' }} />
          {' '}
          <i className="fab fa-cc-amex fa-2x" style={{ color: '#cccccc' }} />
          {' '}
          <i className="fab fa-cc-discover fa-2x" style={{ color: '#cccccc' }} />
          {' '}
          <i className="fab fa-cc-jcb fa-2x" style={{ color: '#cccccc' }} />
          <br /><br />
          <div ref={cardRef} id="card-element" />
          <div ref={addressRef} id="address-element" />
        </div>
      )}

      <div className="checkbox checkbox-primary">
        <input
          id="new-project-accept-terms"
          data-testid="project-list-new-project-accept-terms-checkbox"
          type="checkbox"
          checked={acceptTerms}
          onChange={e => setAcceptTerms(e.target.checked)}
        />
        <label htmlFor="new-project-accept-terms">
          I want this project to be in <b className="label label-success">Active</b> status and accept all billable charges associated.
        </label>
      </div>

      <div className="checkbox checkbox-primary">
        <input
          id="new-project-accept-pricing"
          data-testid="project-list-new-project-accept-pricing-checkbox"
          type="checkbox"
          checked={acceptPricing}
          onChange={e => setAcceptPricing(e.target.checked)}
        />
        <label htmlFor="new-project-accept-pricing">
          I have reviewed and agreed to Folio's pricing.
        </label>
      </div>

      <br /><br />

      {error && <p className="text-danger">{error}</p>}
    </Modal>
  );
}
